import { useMemo } from 'react'
import type { LivestreamEventRow } from '../../hooks/useCompetitiveData'

interface LivestreamFeedProps {
  livestreams: LivestreamEventRow[]
  title?: string
  emptyMessage?: string
}

function formatViewers(value: number): string {
  return Math.round(value || 0).toLocaleString('en-US')
}

export default function LivestreamFeed({
  livestreams,
  title = 'Livestream Events (Last 30 Days)',
  emptyMessage = 'No livestream events detected in the last 30 days',
}: LivestreamFeedProps) {
  const ordered = useMemo(
    () => [...livestreams].sort((left, right) => new Date(right.started_at).getTime() - new Date(left.started_at).getTime()),
    [livestreams]
  )

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">{title}</h3>

      {ordered.length === 0 ? (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">{emptyMessage}</div>
      ) : (
        <div className="mt-3 space-y-2">
          {ordered.map((event) => {
            const isShopEvent = Boolean(event.has_tiktok_shop)

            return (
              <article
                key={event.id}
                className="rounded-lg border p-3"
                style={{
                  borderColor: isShopEvent ? 'var(--warning-300)' : 'var(--gray-200)',
                  background: isShopEvent
                    ? 'linear-gradient(135deg, var(--warning-50) 0%, white 100%)'
                    : 'white',
                }}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{event.competitor_name}</span>
                    <span className="rounded-full border border-[var(--brand-200)] bg-[var(--brand-50)] px-2 py-0.5 text-[11px] font-semibold uppercase text-[var(--brand-700)]">
                      {event.platform}
                    </span>
                    {event.creator_handle && (
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
                        @{event.creator_handle}
                      </span>
                    )}
                    {isShopEvent && (
                      <span className="rounded-full border px-2 py-0.5 text-[11px] font-semibold" style={{ borderColor: 'var(--warning-300)', backgroundColor: 'var(--warning-100)', color: 'var(--warning-800)' }}>
                        TikTok Shop Live
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-slate-500">{new Date(event.started_at).toLocaleString('en-US')}</span>
                </div>

                <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600">
                  <span>Estimated viewers: {formatViewers(event.estimated_viewers)}</span>
                  {event.duration_minutes !== null && <span>Duration: {event.duration_minutes} min</span>}
                </div>

                {event.products_shown.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {event.products_shown.slice(0, 4).map((product) => (
                      <span key={`${event.id}-${product}`} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
                        {product}
                      </span>
                    ))}
                  </div>
                )}
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}
